import * as ActionTypes from './EnumTypes/ActionEnumTypes';
import * as HistoryModule from 'Modules/HistoryModule';
import * as ViewManagementModule from 'Modules/ViewManagementModule';
import * as MapModule from 'Modules/MapModule';
import {Types as UserTypes} from 'Modules/UserModule';
import * as VehiclesModule from 'Modules/VehiclesModule';
import * as HistoryActionModel from './Models/HistoryActionModel';
import {CarRideSummaryDto, PathPoint, RideBoundsCentroid} from 'Models/HistoryModel';
import {polyline, LatLngTuple} from "leaflet";
import {RootState} from "Redux/Reducers/_RootReducer";
import {IPaginatedList} from "Models/PaginatedList";
import {DateRangeValue} from "Models/Dictionary";
import {createSerachQuery} from "Connectors/_main";
import {DEFAULT_PAGE_SIZE_FOR_HISTORY, MAX_ROWS_FOR_FETCH_FULL_LIST, START_FROM} from "../../constants";
import * as ErrorActions from "Redux/Actions/ErrorActions";
import {EnumTypes as ErrorEnumType} from "Models/EnumTypes/ErrorEnumTypes";
import i18n from "Translations/_i18n";
import { UpdateDateRangePayload} from "Redux/Actions/LatestDataframesForm.Actions";

function getCarRidesHistory(params: URLSearchParams) {
  return async dispatch => {
    try {
      dispatch(ViewManagementModule.Actions.showLoader());
      const carRidesHistory = await HistoryModule.Connector.getRideHistory(params);
      const fullPathBoundsCentroid: RideBoundsCentroid = calculateFullRideBounds(carRidesHistory.data.rides);
      dispatch(getCarRidesHistorySuccess(carRidesHistory.data, fullPathBoundsCentroid));
      dispatch(MapModule.Actions.setMapCenter(fullPathBoundsCentroid.centroid));
    } catch (err) {
      dispatch(getCarRidesHistoryFailure(err && err.response ? err.response.data : null));
    } finally {
      dispatch(ViewManagementModule.Actions.hideLoader());
    }
  };
}

function saveRideListIndex(rideListIndex: number) {
  return {
    type: ActionTypes.History.SAVE_RIDE_LIST_INDEX,
    payload: {rideListIndex},
  }
}

function getCarRideDetails(carRideId: number, listIndex: number) {
  return async dispatch => {
    try {
      dispatch(ViewManagementModule.Actions.showDetailsSidebarLoader());
      const carRideDetails = await HistoryModule.Connector.getRideDetails(carRideId);
      const ridePathBoundsCentroid: RideBoundsCentroid = calculateSingleRideBounds(carRideDetails.data.path.reduce((prev,
        current) => prev.concat([[current.latitude, current.longitude]]), []));
      dispatch(getCarRideDetailsSuccess(carRideDetails.data));
      dispatch(saveRideListIndex(listIndex))
      dispatch(setActiveCarRide(carRideId, ridePathBoundsCentroid));
      dispatch(MapModule.Actions.setMapCenter(ridePathBoundsCentroid.centroid));
      dispatch(ViewManagementModule.Actions.hideDetailsSidebarLoader());
    } catch (err) {
      dispatch(setActiveCarRide(carRideId, null));
      dispatch(getCarRideDetailsFailure(err && err.response ? err.response.data : null));
    }
  };
}

function getCarRideDetailsByPositionInRides(carRidePosition: number) {
  return async (dispatch, getState: () => RootState) => {
    try {
      dispatch(ViewManagementModule.Actions.showDetailsSidebarLoader())

      const {rides} = getState().historyStore.carRidesHistorySummary

      await dispatch(getCarRideDetails(rides[carRidePosition].id, carRidePosition))
    } catch (e) {
      // obsługa wewnątrz getCarRideDetails
    }
  }
}

function calculateSingleRideBounds(singlePath: Array<LatLngTuple>): RideBoundsCentroid {
  const fullPolyline = polyline(singlePath).getBounds();
  const polylineCentroid = fullPolyline.getCenter();
  const {lat: swLat, lng: swLng} = fullPolyline.getSouthWest();
  const {lat: neLat, lng: neLng} = fullPolyline.getNorthEast();

  return {bounds: [[swLat, swLng], [neLat, neLng]], centroid: polylineCentroid};
}

function calculateFullRideBounds(rideHistory: Array<CarRideSummaryDto>): RideBoundsCentroid {
  const fullPath: Array<LatLngTuple> = rideHistory.reduce((ridePrev: Array<LatLngTuple>,
    rideCurrent: CarRideSummaryDto) => {
    return ridePrev.concat(rideCurrent.path.reduce((prev: Array<LatLngTuple>, current: PathPoint) => {
      return prev.concat([[current.latitude, current.longitude]]);
    }, []));
  }, []);

  return calculateSingleRideBounds(fullPath);
}

function getTodaysCarRide(carId: number) {
  return async dispatch => {
    try {
      const todaysCarRide = await HistoryModule.Connector.getTodaysRide(carId);
      dispatch(getTodaysCarRideSuccess(todaysCarRide.data, carId));
    } catch (err) {
      dispatch(getTodaysCarRideFailure(err && err.response ? err.response.data : null));
    }
  };
}

type FormData = { dateRange: DateRangeValue; fullTrace: boolean }
type QueryData = FormData & Omit<IPaginatedList, 'results' | 'totalCount'>
function getLatestDataframes(carId: number, queryData: Partial<QueryData>) {
  return async (dispatch, getState: () => RootState) => {
    try {
      const { latestDataframesQuery } = getState().historyStore
      const getWholeRange = queryData.fullTrace
      delete queryData.fullTrace
      dispatch(ViewManagementModule.Actions.showDetailsSidebarLoader());
      const params = createSerachQuery(queryData)

      params.set('first', `${queryData.first ?? START_FROM}`)
      params.set('max', `${getWholeRange ? MAX_ROWS_FOR_FETCH_FULL_LIST : queryData.max ?? DEFAULT_PAGE_SIZE_FOR_HISTORY}`)

      if (!params.has('startDate')) {
        params.set('startDate', latestDataframesQuery.get('startDate'))
      }
      if (!params.has('endDate')) {
        params.set('endDate', latestDataframesQuery.get('endDate'))
      }
      dispatch(saveLatestDataframesQuery(params))
      dispatch(toggleLatestDataframesTableManualHandling(!getWholeRange))
      if (getWholeRange) {
        dispatch(ErrorActions.setNewsForMoment({
          type: ErrorEnumType.NewsType.SUCCESS,
          message: i18n.t('forms:INFO_REQUEST_MAY_LOAD_LONGER')
        }));
      }
      const latestDataframes = await HistoryModule.Connector.getLatestDataframes(carId, params);
      dispatch(getLatestDataframesSuccess(
        latestDataframes.data.dataframes,
        // TODO: Do weryfikacji dlaczego ten obiekt blokuje action i zmmianę danych w tabeli (nie działa paginacja)
        // {
        //   startDate: queryData.dateRange.startDate,
        //   endDate: queryData.dateRange.endDate,
        //   fullTrace: getWholeRange,
        // }
      ));
      dispatch(ViewManagementModule.Actions.hideDetailsSidebarLoader());
    } catch (err) {
      dispatch(ViewManagementModule.Actions.hideDetailsSidebarLoader());
      dispatch(getLatestDataframesFailure(err));
    }
  };
}

function saveLatestDataframesQuery(latestDataframesQuery: URLSearchParams) {
  return {
    type: ActionTypes.History.SAVE_LATEST_DATAFRAMES_QUERY,
    payload: {
      latestDataframesQuery
    }
  }
}

function toggleLatestDataframesTableManualHandling(latestDataframesManualHandling: boolean) {
  return {
    type: ActionTypes.History.TOGGLE_LATEST_DATAFRAMES_MANUAL_HANDLING,
    payload: { latestDataframesManualHandling }
  }
}

function clearLastDataframesQuery() {
  return (dispatch) => {
    dispatch(saveLatestDataframesQuery(null))
  }
}

// SUCCESS | FAILURE ACTIONS
function getLatestDataframesSuccess(dataframes: IPaginatedList<VehiclesModule.Types.CarDataframeDto>, formData?: UpdateDateRangePayload): HistoryActionModel.GetLatestDataframesSuccess {
  return {
    type: ActionTypes.History.GET_LATEST_DATAFRAMES_SUCCESS,
    payload: {
      dataframes,
      formData
    }
  };
}

function getLatestDataframesFailure(err: any): HistoryActionModel.GetLatestDataframesFailure {
  return {
    type: ActionTypes.History.GET_LATEST_DATAFRAMES_FAILURE
  };
}

function getCarRidesHistorySuccess(carRidesHistorySummary: HistoryModule.Types.CarRidesHistorySummaryResponse,
  fullPathBoundsCentroid: RideBoundsCentroid): HistoryActionModel.GetCarRidesHistorySuccessAction {
  return {
    type: ActionTypes.History.GET_CAR_RIDES_HISTORY_SUCCESS,
    payload: {
      carRidesHistorySummary,
      fullPathBoundsCentroid
    }
  };
}

function getCarRidesHistoryFailure(err: UserTypes.Error): HistoryActionModel.GetCarRidesHistoryFailuresAction {
  return {
    type: ActionTypes.History.GET_CAR_RIDES_HISTORY_FAILURE,
    payload: {
      error: err
    }
  };
}

function getCarRideDetailsSuccess(carRideDetails: HistoryModule.Types.CarRideDetailsResponse): HistoryActionModel.GetCarRideDetailsSuccessAction {
  return {
    type: ActionTypes.History.GET_CAR_RIDE_DETAILS_SUCCESS,
    payload: {
      carRideDetails
    }
  };
}

function getCarRideDetailsFailure(err: UserTypes.Error): HistoryActionModel.GetCarRideDetailsFailuresAction {
  return {
    type: ActionTypes.History.GET_CAR_RIDE_DETAILS_FAILURE,
    payload: {
      error: err
    }
  };
}

function setActiveCarRide(carRideId: number,
  fullPathBoundsCentroid: RideBoundsCentroid): HistoryActionModel.SetActiveCarRideAction {
  return {
    type: ActionTypes.History.SET_ACTIVE_CARRIDE,
    payload: {
      carRideId,
      fullPathBoundsCentroid
    }
  };
}

function getTodaysCarRideSuccess(carTodaysRide: HistoryModule.Types.CarRideTodaysSummaryDto,
  currentCarIdRides: number): HistoryActionModel.GetCarTodaysRideSuccessAction {
  return {
    type: ActionTypes.History.GET_CAR_TODAYS_RIDE_SUCCESS,
    payload: {
      carTodaysRide,
      currentCarIdRides
    }
  };
}

function getTodaysCarRideFailure(err: UserTypes.Error): HistoryActionModel.GetCarTodaysRideFailureAction {
  return {
    type: ActionTypes.History.GET_CAR_TODAYS_RIDE_FAILURE,
    payload: {
      error: err
    }
  };
}

function clearRideDetails(): HistoryActionModel.ClearRideDetails {
  return {
    type: ActionTypes.History.CLEAR_RIDE_DETAILS,
  }
}

function clearHistoryRides() {
  return {
    type: ActionTypes.History.CLEAR_HISTORY_RIDES
  }
}

function clearHistoryRidesSubmission() {
  return {
    type: ActionTypes.History.CLEAR_HISTORY_RIDES_SUBMISSION
  }
}
export {
  getCarRidesHistory,
  getCarRideDetails,
  setActiveCarRide,
  getTodaysCarRide,
  getLatestDataframes,
  getLatestDataframesSuccess,
  getCarRideDetailsByPositionInRides,
  clearRideDetails,
  clearHistoryRides,
  clearHistoryRidesSubmission,
  clearLastDataframesQuery,
  toggleLatestDataframesTableManualHandling
};



// WEBPACK FOOTER //
// ./src/Redux/Actions/HistoryActions.ts